import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, ClipboardPaste, Copy, Trash2 } from 'lucide-react'
import { centZuBetrag, eingangZeileNettoCent, erfuelltRecht, geldText, type IncomingCostTarget } from '@symphenia/shared-types'
import { GeldFeld, GeldFeldFuer, geldEingabe } from '@/components/geldfeld'
import { KlappUmschalter, useKlappModus } from '@/components/klappmodus'
import { Feld as Spalte, RasterFeld, RasterInfo, RasterZelle, Vorschlaege, ZELLE_RAHMEN, ZeilenBlock, ZeilenListe, leseZahl, useRaster, useSuche, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { bestaetige } from '@/lib/dialoge'
import { eingabeOffen, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { confirmPairings, createIncomingLine, deleteIncomingLine, lookupArticles, lookupPositions, lookupProjects, pasteIncomingLines, patchIncomingLine, reorderIncomingLines, setLineSource } from '../api/incoming-invoices.api'
import { ABWEICHUNG_TEXT, kostenzielText, mengeText } from '../lib/texte'
import { beobachteZeilenAblage, leseZeilenAblage, schreibeZeilenAblage } from '../lib/zeilen-ablage'
import type { IncomingFrame, IncomingInvoiceDetail, IncomingInvoiceLine, LookupArticle, LookupPosition, LookupProject } from '../types/incoming-invoices.types'
import { KontenVorschlagen, KontoVorschlag, useKontovorschlag } from './Kontovorschlag'
import { KontoOptionen, kontoText } from './Sachkonto'

const ZIELE: IncomingCostTarget[] = ['PROJECT', 'STOCK', 'OVERHEAD']

/**
 * **Zeile 1** ist, was auf der Rechnung steht: Menge · Einheit · Bezeichnung · EP · PE · Rabatt · Betrag.
 * **Darunter** steht, wie die Position gebucht wird: Steuer · Kostenziel · Sachkonto unter der Bezeichnung,
 * rechts daneben das Projekt bzw. die Kategorie (`Unterzeile`). Die weiteren Zeilen klappen beim Bearbeiten auf.
 *
 * Bis zum 6.10.2026 standen Steuer, Kostenziel und Sachkonto als Spalten in Zeile 1 (Anordnung der Stückliste,
 * Auftraggeber 4.10.2026). Der Bezeichnung blieben damit bei 900 px Breite 144 px — zu schmal (Auftraggeber
 * 6.10.2026). So bleiben ihr über 400, und das Sachkonto zeigt neben dem Kostenziel auch seine Bezeichnung.
 * Das Raster hält jetzt ab 696 px Breite (vorher 768, mit der Sachkonto-Spalte 856) — darunter bricht die
 * Zeile um, und jedes Feld trägt seine Beschriftung; die Bezeichnung ist im Raster nie schmäler als 216 px.
 */
const spaltenFuer = (mitKonto: boolean): SpaltenDef[] => [
  { key: 'quantity', label: 'Menge', spur: '4rem', min: 60, schmal: 'w-20', rechts: true },
  { key: 'unitCode', label: 'Einheit', spur: '3.5rem', min: 56, schmal: 'w-20' },
  {
    key: 'name',
    label: 'Bezeichnung',
    kopf: (
      <>
        Bezeichnung <span className="opacity-70">· darunter Steuer, Kostenziel{mitKonto ? ', Sachkonto' : ''}</span>
      </>
    ),
    spur: 'minmax(13.5rem,1fr)',
    // 20 px mehr als die Spur: Die übrigen `min` liegen zusammen so viel unter ihren Spuren — sonst hielte das
    // Raster schon, bevor die Bezeichnung ihre 216 px wirklich hat, und die Zeile liefe über den Rand.
    min: 236,
    schmal: 'w-72',
  },
  { key: 'unitPrice', label: 'EP', spur: '5rem', min: 72, schmal: 'w-24', rechts: true },
  { key: 'priceUnit', label: 'PE', spur: '3rem', min: 48, schmal: 'w-16', rechts: true },
  { key: 'discountPercent', label: 'Rab.', spur: '2.75rem', min: 40, schmal: 'w-14', rechts: true },
  { key: 'lineNet', label: 'Netto', spur: '5rem', min: 72, schmal: 'w-24', rechts: true },
  { key: 'aktion', label: '', spur: '1.75rem', min: 24, schmal: 'w-8' },
]

/**
 * Ein Feld der Unterzeile — kleiner als Zeile 1, damit sie als Zusatz zur Position liest. Gesperrt verliert
 * eine Auswahl auch ihren Pfeil: An einer freigegebenen Rechnung steht dort Text, kein Bedienelement.
 */
const klein = `${ZELLE_RAHMEN} px-1 py-0.5 text-xs disabled:appearance-none disabled:text-foreground`

/**
 * Die **Unterzeile** einer Position: Steuer · Kostenziel · Sachkonto unter der Bezeichnung; unter Preis und
 * Betrag, wohin das Kostenziel zeigt (Projekt bzw. Gemeinkosten-Kategorie). Im Umbruch trägt jedes Feld
 * seine Beschriftung wie alle anderen.
 */
function Unterzeile({ steuer, ziel, konto, zuordnung, zuordnungLabel }: { steuer: ReactNode; ziel: ReactNode; konto: ReactNode; zuordnung: ReactNode; zuordnungLabel: string }) {
  const raster = useRaster()
  if (!raster) {
    return (
      <>
        <RasterFeld von="name" label="Steuer" schmal="w-28">
          {steuer}
        </RasterFeld>
        <RasterFeld von="name" label="Kostenziel" schmal="w-32">
          {ziel}
        </RasterFeld>
        {konto && (
          <RasterFeld von="name" label="Sachkonto" schmal="w-56">
            {konto}
          </RasterFeld>
        )}
        {zuordnung && (
          <RasterFeld von="name" label={zuordnungLabel} schmal="w-72">
            {zuordnung}
          </RasterFeld>
        )}
      </>
    )
  }
  // Eine Zelle von der Bezeichnung bis zum Betrag. Das letzte Feld wächst zuerst und höchstens so breit wie EP
  // bis Netto (17.25rem): Hat alles Platz, endet das Sachkonto genau unter der Bezeichnung und die Zuordnung
  // steht unter dem Preis. Wird es enger, gibt die Zuordnung Breite ab (bis 10rem); erst ganz schmal rückt sie
  // in eine eigene Zeile. Umgebrochen wird nach der Ausgangsbreite — darum ist die klein und das Wachsen groß.
  return (
    <RasterZelle von="name" bis="lineNet" className="flex flex-wrap gap-x-2 gap-y-1">
      <span className="w-24 shrink-0">{steuer}</span>
      <span className="w-28 shrink-0">{ziel}</span>
      {konto && <span className="min-w-[9rem] flex-1">{konto}</span>}
      <span className={`ml-auto max-w-[17.25rem] ${zuordnung ? 'min-w-[10rem] flex-[999_1_10rem]' : 'min-w-0 flex-[999_1_0%]'}`}>{zuordnung}</span>
    </RasterZelle>
  )
}

/**
 * AP-34 6., Nachtrag 2 2.4 — **Positionen** als Zeilenblock: Jede Zeile ist eine Speicherstelle
 * und speichert beim Verlassen; die leere letzte Zeile legt an. Kostenziel und Zuordnung stehen
 * zusammen in der Unterzeile — Projekt, Gemeinkosten mit Kategorie, Lager ohne Zuordnung.
 * Beim Bearbeiten klappen zwei Zeilen auf: Alternativmenge, Langtext und die Position des Projekts; darunter
 * Gewicht und die Artikelnummern. Zugeklappt steht davon eine Kurzinfo unter der Zeile.
 *
 * Seit dem 5.10.2026 (Auftraggeber): Die neue Zeile zeigt **alle** Felder sofort, nicht erst nach dem
 * Anlegen; und die Liste trägt die Werkzeuge der Stückliste — am linken Rand markieren, an der freien
 * Fläche ziehen, kopieren (Strg+C), einfügen (Strg+V), mehrere löschen.
 */
export function PositionenBlock({
  rechnung,
  rahmen,
  onRechnung,
  onFehler,
}: {
  rechnung: IncomingInvoiceDetail
  rahmen: IncomingFrame
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
}) {
  // Was nicht eingerichtet ist, wird nicht angeboten: Das Sachkonto erscheint mit dem ersten Konto des Stamms
  // (oder im Modus „kontiert", wo das Konto Pflicht ist — dort ohne Stamm als freier Text wie zuvor).
  const mitKonto = rahmen.bookkeepingMode === 'POSTED' || rahmen.ledgerAccounts.length > 0
  const spalten = useMemo(() => spaltenFuer(mitKonto), [mitKonto])
  const darf = rechnung.editable
  const userId = useAuthStore((s) => s.user?.id)
  const behaelter = useRef<HTMLDivElement>(null)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [hinweis, setHinweis] = useState<string | null>(null)
  // Auftraggeber 10.10.2026: Positionen immer aufgeklappt, immer zugeklappt oder beim Bearbeiten — je Benutzer gemerkt.
  // „Immer aufgeklappt“ löst „Alle Positionen aufklappen“ ab (Nachtrag 6, PT-25); an der gesperrten Rechnung
  // bleibt in „beim Bearbeiten“ der Pfeil je Position.
  const [klappModus, setKlappModus] = useKlappModus('incoming_lines')
  const [ablage, setAblage] = useState(() => leseZeilenAblage(userId))
  useEffect(() => {
    setAblage(leseZeilenAblage(userId))
    return beobachteZeilenAblage(() => setAblage(leseZeilenAblage(userId)))
  }, [userId])

  const zeilen = rechnung.lines
  const reihe = useMemo(() => zeilen.map((z) => z.id), [zeilen])
  const auswahl = reihe.filter((id) => markiert.has(id))

  const tue = async (was: () => Promise<IncomingInvoiceDetail>, fehlertext: string, danach?: string) => {
    try {
      onFehler(null)
      onRechnung(await was())
      setHinweis(danach ?? null)
      return true
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
      return false
    }
  }

  /** Neue Reihenfolge an den Server; bis zur Antwort steht sie schon so da. */
  const ordne = async (ids: readonly string[]) => {
    const nach = new Map(zeilen.map((z) => [z.id, z]))
    const vorher = rechnung
    onRechnung({ ...rechnung, lines: ids.map((id) => nach.get(id)!).filter(Boolean) })
    if (!(await tue(() => reorderIncomingLines(rechnung.id, [...ids]), 'Die Reihenfolge ließ sich nicht speichern.'))) onRechnung(vorher)
  }
  /** ↑ ↓ — die markierten Zeilen je um eine Stelle, Blöcke bleiben zusammen (der Tastaturweg zum Ziehen). */
  const schiebe = (richtung: -1 | 1) => {
    if (!auswahl.length) return
    const r = richtung < 0 ? [...reihe] : [...reihe].reverse()
    for (let i = 1; i < r.length; i++) if (markiert.has(r[i]) && !markiert.has(r[i - 1])) [r[i - 1], r[i]] = [r[i], r[i - 1]]
    void ordne(richtung < 0 ? r : r.reverse())
  }
  /** Mit der gegriffenen wandern alle markierten Zeilen — ist sie selbst markiert. */
  const mitGezogen = (key: string) => (markiert.has(key) ? auswahl : [key])
  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      const neu = verschiebeMehrereVor(reihe, mitGezogen(key), vorKey)
      if (neu !== reihe) void ordne(neu)
    },
  })

  const kopieren = () => {
    if (!auswahl.length || !userId) return
    schreibeZeilenAblage({ userId, lineIds: auswahl, herkunft: rechnung.number ? `Eingangsrechnung ${rechnung.number}` : 'einer neuen Eingangsrechnung' })
    onFehler(null)
    setHinweis(`${auswahl.length} ${auswahl.length === 1 ? 'Position' : 'Positionen'} kopiert — hier oder in einer anderen Eingangsrechnung einfügen (Strg+V).`)
  }
  /** Vor der ersten markierten Zeile, sonst am Ende. Der Bezug zur Bestellung wandert nicht mit. */
  const einfuegen = async () => {
    if (!ablage || !darf) return
    const n = ablage.lineIds.length
    if (await tue(() => pasteIncomingLines(rechnung.id, ablage.lineIds, auswahl[0] ?? null), 'Die Positionen ließen sich nicht einfügen.', `${n} ${n === 1 ? 'Position' : 'Positionen'} eingefügt.`)) setMarkiert(new Set())
  }
  const loeschen = async () => {
    const n = auswahl.length
    if (!n || !(await bestaetige(`${n} ${n === 1 ? 'Position' : 'Positionen'} löschen? Das lässt sich nicht rückgängig machen.`, { ok: 'Löschen', gefaehrlich: true }))) return
    // Zeile für Zeile über den einen Löschweg; bricht eine ab, bleiben die davor gelöscht.
    for (const id of auswahl) if (!(await tue(() => deleteIncomingLine(rechnung.id, id), 'Die Position ließ sich nicht löschen.'))) break
    setMarkiert(new Set())
  }

  const { amRand } = useRandMarkieren({ behaelter, reihe, markiert, setMarkiert, onKopieren: kopieren })

  // Strg+V fügt ein, solange kein Feld den Fokus hat — sonst bleibt es Text einfügen.
  const einfuegenRef = useRef(einfuegen)
  einfuegenRef.current = einfuegen
  useEffect(() => {
    if (!ablage || !darf) return
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'v') return
      const fokus = document.activeElement
      if (fokus instanceof Element && fokus.closest('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"], [role="dialog"]')) return
      if (!behaelter.current?.getClientRects().length) return
      e.preventDefault()
      void einfuegenRef.current()
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
  }, [ablage, darf])

  const gezogeneZeile = gezogen ? zeilen.find((z) => z.id === gezogen) : undefined
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])
  const werkzeug = 'inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted disabled:opacity-50'

  return (
    <div className="space-y-2">
      {/* AP-44 7.3: die KI schlägt Sachkonten vor, wo die Stammdaten keines geben — nur mit Baustein und Sachkontenstamm */}
      <KontenVorschlagen rechnung={rechnung} rahmen={rahmen} onRechnung={onRechnung} onFehler={onFehler} />
      {/* Werkzeuge der Liste wie in der Stückliste: am linken Rand markieren, an der freien Fläche ziehen */}
      {(auswahl.length > 0 || (ablage && darf)) && (
        <div className="flex min-h-[2.125rem] flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
          {auswahl.length > 0 && (
            <>
              <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
              {darf && (
                <>
                  <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach oben" title="nach oben" onClick={() => schiebe(-1)}>
                    <ArrowUp size={14} />
                  </button>
                  <button type="button" className="rounded border border-border p-1 hover:bg-muted" aria-label="nach unten" title="nach unten" onClick={() => schiebe(1)}>
                    <ArrowDown size={14} />
                  </button>
                </>
              )}
              <button type="button" className={werkzeug} aria-label="Kopieren" title="Kopieren (Strg+C)" onClick={kopieren}>
                <Copy size={13} /> Kopieren
              </button>
              {darf && (
                <button type="button" className={`${werkzeug} text-destructive`} onClick={() => void loeschen()}>
                  <Trash2 size={13} /> Löschen
                </button>
              )}
            </>
          )}
          {ablage && darf && (
            <button
              type="button"
              className={`${werkzeug} ml-auto`}
              // Der Name bleibt „Einfügen" — ein `title` allein würde ihn für Vorlesehilfen ersetzen.
              aria-label={`Einfügen (${ablage.lineIds.length})`}
              title={`${ablage.lineIds.length} ${ablage.lineIds.length === 1 ? 'Position' : 'Positionen'} aus ${ablage.herkunft} — ${auswahl.length ? 'vor der ersten markierten Position' : 'am Ende'} (Strg+V)`}
              onClick={() => void einfuegen()}
            >
              <ClipboardPaste size={13} /> Einfügen ({ablage.lineIds.length})
            </button>
          )}
        </div>
      )}
      {hinweis && <p className="text-xs text-muted-foreground">{hinweis}</p>}
      {(darf || zeilen.length > 0) && (
        <div className="flex justify-end">
          <KlappUmschalter modus={klappModus} onModus={setKlappModus} />
        </div>
      )}

      <datalist id={EINHEITEN_LISTE}>
        {rahmen.units.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>

      <div ref={behaelter}>
        <ZiehBereich
          dndProps={dndProps}
          vorschau={
            gezogeneZeile && (
              <>
                {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Zeilen</span>}
                <span className="tabular-nums text-muted-foreground">
                  {mengeText(gezogeneZeile.quantity)} {gezogeneZeile.unitCode}
                </span>{' '}
                <span className="font-medium">{gezogeneZeile.name}</span>
              </>
            )
          }
        >
          <ZeilenListe spalten={spalten} klappModus={klappModus}>
            {zeilen.length === 0 && !darf && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Keine Positionen.</p>}
            {zeilen.map((z) => (
              <Zeile
                key={z.id}
                rechnung={rechnung}
                zeile={z}
                rahmen={rahmen}
                mitKonto={mitKonto}
                onRechnung={onRechnung}
                onFehler={onFehler}
                markiert={markiert.has(z.id)}
                gezogen={gezogeneIds.has(z.id)}
                ziellinie={ziel?.key === z.id ? ziel.kante : null}
                onRand={amRand(z.id)}
              />
            ))}
            {darf && <NeueZeile rechnung={rechnung} rahmen={rahmen} mitKonto={mitKonto} onRechnung={onRechnung} onFehler={onFehler} />}
          </ZeilenListe>
        </ZiehBereich>
      </div>
    </div>
  )
}

const EINHEITEN_LISTE = 'eingangsrechnung-einheiten'

/**
 * Der Nettobetrag einer Zeile aus dem, was gerade in ihr steht — mit der Formel des Servers
 * (`eingangZeileNettoCent`), damit der Betrag beim Speichern nicht springt. Eine leere oder unlesbare Zahl
 * zählt 0, eine fehlende Preiseinheit 1; geprüft wird wie bisher beim Verlassen der Zeile.
 */
const zeilenNetto = (menge: number | null, preis: number | null, pe: number | null, rabatt: number | null) =>
  centZuBetrag(eingangZeileNettoCent(menge ?? 0, preis ?? 0, pe !== null && pe >= 1 ? Math.round(pe) : 1, rabatt))

function Zeile({
  rechnung,
  zeile,
  rahmen,
  mitKonto,
  onRechnung,
  onFehler,
  markiert,
  gezogen,
  ziellinie,
  onRand,
}: {
  rechnung: IncomingInvoiceDetail
  zeile: IncomingInvoiceLine
  rahmen: IncomingFrame
  mitKonto: boolean
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
  markiert: boolean
  gezogen: boolean
  ziellinie: 'oben' | 'unten' | null
  onRand: (e: ReactMouseEvent) => void
}) {
  const terms = useTerminology()
  const gesperrt = !rechnung.editable
  const stelle = useSpeicherstelle<IncomingInvoiceDetail>({
    schluessel: `incoming-invoice-line:${zeile.id}`,
    bezeichnung: `Position ${zeile.positionNo}`,
    daten: { lines: [zeile] } as unknown as IncomingInvoiceDetail,
    // Die Antwort ist die ganze Rechnung: Summen, Steuer und Prüfung stimmen danach wieder.
    schreibe: async (patch, stand) => {
      const r = await patchIncomingLine(rechnung.id, zeile.id, patch, stand)
      onRechnung(r)
      return r
    },
    werte: (a) => ((a.lines ?? []).find((x) => x.id === zeile.id) ?? zeile) as unknown as Datensatz,
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: `Position ${zeile.positionNo}` })
  const opt = { gruppe, gesperrt }
  const name = useFeld(stelle, 'name', { ...opt, label: 'Bezeichnung' })
  const langtext = useFeld(stelle, 'description', { ...opt, label: 'Langtext' })
  const artikel = useFeld(stelle, 'articleId', { ...opt, art: 'auswahl', label: 'Artikel' })
  // Artikel suchen wie in der Stückliste: Die Bezeichnung bleibt frei, ein Treffer hängt den Artikel an die Zeile.
  const [sucheName, setSucheName] = useState(false)
  const [artikelTreffer, setArtikelTreffer] = useSuche(String(name.props.value ?? ''), sucheName && !gesperrt, lookupArticles)
  const uebernimm = (a: LookupArticle) => {
    setSucheName(false)
    setArtikelTreffer([])
    name.tippe(a.name)
    if (a.documentText && !zeile.description) langtext.tippe(a.documentText)
    if (!zeile.unitCode) einheit.tippe(a.unitCode)
    artikel.tippe(a.id)
  }
  const menge = useFeld<number | null>(stelle, 'quantity', { ...opt, art: 'zahl', stellen: 3, pflicht: true, label: 'Menge' })
  const einheit = useFeld(stelle, 'unitCode', { ...opt, label: 'Einheit' })
  const altMenge = useFeld<number | null>(stelle, 'altQuantity', { ...opt, art: 'zahl', stellen: 3, label: 'Alternativmenge' })
  const altEinheit = useFeld(stelle, 'altUnitCode', { ...opt, label: 'Alternativeinheit' })
  const gewicht = useFeld<number | null>(stelle, 'weightPerUnit', { ...opt, art: 'zahl', stellen: 4, label: 'Gewicht', pruefe: (w) => (w !== null && w < 0 ? 'nicht negativ' : null) })
  const lieferantNr = useFeld(stelle, 'supplierArticleNumber', { ...opt, label: 'Lieferanten-Artikelnummer' })
  // Preisformat (Auftraggeber 6.10.2026): ohne Fokus `1.234,50`, beim Bearbeiten ohne Tausenderpunkt; bis zu vier Stellen, wo der Preis sie trägt
  const preis = useFeld<number | null>(stelle, 'unitPrice', { ...opt, art: 'zahl', pflicht: true, label: 'Einzelpreis', schreib: (w) => geldEingabe(w, 4) })
  const pe = useFeld<number | null>(stelle, 'priceUnit', { ...opt, art: 'zahl', stellen: 0, pflicht: true, label: 'Preiseinheit', pruefe: (w) => (w !== null && w < 1 ? 'mindestens 1' : null) })
  const rabatt = useFeld<number | null>(stelle, 'discountPercent', { ...opt, art: 'zahl', stellen: 2, label: 'Rabatt' })
  // Der Betrag rechnet mit, sobald Menge, Preis, Preiseinheit oder Rabatt getippt sind — nicht erst, wenn die
  // Zeile verlassen und gespeichert ist (Auftraggeber 7.10.2026). Ohne offene Eingabe gilt der Betrag des Servers.
  const netto = eingabeOffen(menge, preis, pe, rabatt) ? zeilenNetto(menge.wert, preis.wert, pe.wert, rabatt.wert) : zeile.lineNet
  const steuer = useFeld(stelle, 'taxCodeKey', { ...opt, art: 'auswahl', label: 'Steuer' })
  const ziel = useFeld<IncomingCostTarget | null>(stelle, 'costTarget', { ...opt, art: 'auswahl', label: 'Kostenziel' })
  const kategorie = useFeld(stelle, 'overheadCategoryId', { ...opt, art: 'auswahl', label: 'Kategorie' })
  const position = useFeld(stelle, 'projectPositionId', { ...opt, art: 'auswahl', label: terms.position })
  const projekt = useFeld(stelle, 'projectId', { ...opt, art: 'auswahl', label: terms.project })
  // Mit Sachkontenstamm eine Auswahl (speichert sofort), ohne ihn freier Text wie zuvor
  const mitStamm = rahmen.ledgerAccounts.length > 0
  const konto = useFeld(stelle, 'expenseAccount', { ...opt, art: mitStamm ? 'auswahl' : 'text', label: 'Sachkonto' })
  const rechnungKonto = rechnung.defaultExpenseAccount
  const kontoWeichtAb = !!rechnungKonto && !!zeile.expenseAccount && zeile.expenseAccount !== rechnungKonto
  // Nachtrag 6, PT-26: Führt der Betrieb Sachkonten, fällt eine Position ohne Konto auf — die Übergabe an BMD verlangt es.
  const kontoFehlt = mitStamm && zeile.lineType !== 'TEXT' && !zeile.expenseAccount
  const kontoHerkunft = !zeile.expenseAccount
    ? 'kein Sachkonto'
    : zeile.expenseAccountSource === 'INVOICE'
      ? 'Sachkonto der Rechnung'
      : zeile.expenseAccountSource === 'MANUAL'
        ? kontoWeichtAb
          ? 'von Hand gesetzt — weicht vom Sachkonto der Rechnung ab'
          : 'von Hand gesetzt'
        : zeile.expenseAccountSource === 'AI'
          ? 'Vorschlag der KI, übernommen'
          : 'vorgeschlagen'
  // AP-44 7.6: der offene Vorschlag der KI steht neben dem Konto — an einer gesperrten Rechnung gibt es nichts zu übernehmen
  const kiKonto = useKontovorschlag(rechnung, onRechnung, onFehler)
  const kiVorschlag = mitKonto && !gesperrt ? zeile.accountSuggestion : null
  const kontoTitel = [kontoText(rahmen.ledgerAccounts, zeile.expenseAccount), kontoHerkunft].filter(Boolean).join(' — ')
  // 9.6 — schreibt bei der Freigabe in den Artikelstamm: bedienbar nur mit dessen Recht
  const darfArtikel = erfuelltRecht('articles.article.manage', useAuthStore((s) => s.user?.permissions))
  const preisUebernehmen = useFeld<boolean>(stelle, 'updatePurchasePrice', { gesperrt: gesperrt || !darfArtikel, art: 'schalter', label: 'Einkaufspreis übernehmen' })
  const text = zeile.lineType === 'TEXT'
  const mitProjekt = !text && ziel.wert === 'PROJECT'
  const positionen = usePositionen(mitProjekt && rahmen.projectPositions ? projekt.wert : null)

  const loeschen = async () => {
    try {
      onFehler(null)
      onRechnung(await deleteIncomingLine(rechnung.id, zeile.id))
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Position ließ sich nicht löschen.'))
    }
  }
  const bezug = async (was: () => Promise<IncomingInvoiceDetail>) => {
    try {
      onFehler(null)
      onRechnung(await was())
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Zuordnung ließ sich nicht ändern.'))
    }
  }

  // ── Unterzeile: wie die Position gebucht wird — immer zu sehen, direkt unter der Bezeichnung ──
  const steuerFeld = (
    <select aria-label="Steuercode" title={rahmen.taxCodes.find((c) => c.key === steuer.wert)?.label ?? 'Steuercode'} className={klein} {...steuer.props}>
      <option value="">{gesperrt ? 'ohne Steuer' : '— Steuer'}</option>
      {zeile.taxCodeKey && !rahmen.taxCodes.some((c) => c.key === zeile.taxCodeKey) && <option value={zeile.taxCodeKey}>{zeile.taxCodeKey}</option>}
      {rahmen.taxCodes.map((c) => (
        <option key={c.key} value={c.key} title={c.label}>
          {c.key}
        </option>
      ))}
    </select>
  )
  const zielFeld = (
    <select aria-label="Kostenziel" className={klein} {...ziel.props}>
      <option value="">{gesperrt ? 'ohne Kostenziel' : '— Kostenziel'}</option>
      {ZIELE.map((z) => (
        <option key={z} value={z}>
          {kostenzielText(z, terms.project)}
        </option>
      ))}
    </select>
  )
  // Fett, was vom Sachkonto der Rechnung abweicht — so fällt es in der Liste auf.
  const kontoFeld = !mitKonto ? null : mitStamm ? (
    <select aria-label="Sachkonto" title={kontoTitel} className={`${klein} tabular-nums ${kontoWeichtAb ? 'font-semibold' : ''} ${kontoFehlt ? 'font-semibold !text-amber-700 dark:!text-amber-300' : ''}`} {...konto.props}>
      {/* An einer gesperrten Rechnung ist die leere Auswahl eine Aussage, keine Aufforderung. Der Abzug einer
          Schlussrechnung folgt dem Sachkonto der Rechnung nicht — dort stünde „wie Rechnung" über einem leeren Konto. */}
      <option value="">{gesperrt ? 'ohne Sachkonto' : rechnungKonto && zeile.lineType !== 'DEDUCTION' ? `wie Rechnung (${rechnungKonto})` : '— Sachkonto'}</option>
      <KontoOptionen konten={rahmen.ledgerAccounts} aktuell={zeile.expenseAccount} />
    </select>
  ) : (
    <input aria-label="Sachkonto" placeholder="Sachkonto" title={kontoTitel} className={`${klein} tabular-nums`} {...konto.props} />
  )
  // Wohin das Kostenziel zeigt: Projekt (Suche) oder Gemeinkosten-Kategorie — das Lager braucht nichts.
  const zuordnungFeld = mitProjekt ? (
    <ProjektFeld klasse={klein} gesperrt={gesperrt} anzeige={[zeile.projectNumber, zeile.projectName].filter(Boolean).join(' ')} gewaehlt={projekt.wert} onWahl={(p) => projekt.setze(p.id)} label={terms.project} />
  ) : ziel.wert === 'OVERHEAD' ? (
    <select aria-label="Gemeinkosten-Kategorie" className={klein} {...kategorie.props}>
      <option value="">{gesperrt ? 'ohne Kategorie' : 'Kategorie wählen …'}</option>
      {/* Eine inzwischen abgeschaltete Kategorie steht nicht mehr in der Auswahl — die Zeile trägt sie trotzdem */}
      {zeile.overheadCategoryId && !rahmen.overheadCategories.some((k) => k.id === zeile.overheadCategoryId) && <option value={zeile.overheadCategoryId}>{zeile.overheadCategoryName ?? '—'}</option>}
      {rahmen.overheadCategories.map((k) => (
        <option key={k.id} value={k.id}>
          {k.name}
        </option>
      ))}
    </select>
  ) : null
  const unterzeile = text ? undefined : <Unterzeile steuer={steuerFeld} ziel={zielFeld} konto={kontoFeld} zuordnung={zuordnungFeld} zuordnungLabel={mitProjekt ? terms.project : 'Kategorie'} />

  // ── Beim Bearbeiten klappen zwei weitere Zeilen auf, spaltengenau unter Zeile 1 ──
  const langtextFeld = (
    <RasterFeld von="name" label="Langtext" schmal="w-full max-w-xl">
      {gesperrt ? (
        // Ein gesperrtes Textfeld zeigte zwei Zeilen und ließe sich nicht größer ziehen — als Text steht alles da.
        <span className="block whitespace-pre-wrap break-words px-1.5 py-1 text-sm">{zeile.description || <span className="text-muted-foreground">—</span>}</span>
      ) : (
        <textarea rows={2} className={zelle} {...langtext.props} />
      )}
    </RasterFeld>
  )
  const zeile2 = text ? (
    langtextFeld
  ) : (
    <>
      <RasterFeld von="quantity" label="Alternativmenge" schmal="w-24">
        <input className={`${zelle} text-right`} inputMode="decimal" {...altMenge.props} />
      </RasterFeld>
      <RasterFeld von="unitCode" label={null} schmal="w-20">
        <input aria-label="Alternativeinheit" list={EINHEITEN_LISTE} className={zelle} {...altEinheit.props} />
      </RasterFeld>
      {langtextFeld}
      {/* unter dem Projekt der Unterzeile */}
      {mitProjekt && rahmen.projectPositions && positionen.length > 0 && (
        <RasterFeld von="unitPrice" bis="lineNet" label={terms.position} schmal="w-56">
          <select className={zelle} {...position.props}>
            <option value="">ohne {terms.position}</option>
            {positionen.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number} {p.name}
              </option>
            ))}
          </select>
        </RasterFeld>
      )}
    </>
  )
  const zeile3 = !text && (
    <>
      <RasterFeld von="quantity" bis="unitCode" label="kg je Einheit" schmal="w-40">
        <span className="flex items-center gap-1.5">
          <input className={`${zelle} text-right`} inputMode="decimal" {...gewicht.props} />
          <span className="whitespace-nowrap text-xs text-muted-foreground">kg / {zeile.unitCode || 'Einheit'}</span>
        </span>
      </RasterFeld>
      <RasterFeld von="name" label="ArtNr · Lief.-ArtNr" schmal="w-full max-w-xl">
        <span className="flex items-center gap-2">
          <span className="w-36 shrink-0 truncate px-1.5 py-1 font-mono text-sm" title={zeile.articleNumber ?? 'Kommt aus dem Artikelstamm — in der Bezeichnung einen Artikel suchen und wählen'}>
            {zeile.articleNumber ?? <span className="text-muted-foreground">—</span>}
          </span>
          <input aria-label="Lieferanten-Artikelnummer" placeholder="Artikelnummer des Lieferanten" className={`${zelle} min-w-0 flex-1`} title="Artikelnummer des Lieferanten" {...lieferantNr.props} />
        </span>
      </RasterFeld>
    </>
  )

  // Zugeklappt: Alternativmenge unter der Menge, die Nummern unter der Unterzeile, Projektposition und Gewicht rechts.
  const positionText = zeile.costTarget === 'PROJECT' && zeile.projectPositionNumber ? `${terms.position} ${zeile.projectPositionNumber} ${zeile.projectPositionName ?? ''}`.trim() : ''
  const gewichtText = zeile.weightPerUnit !== null ? `${zeile.weightPerUnit.toLocaleString('de-DE', { maximumFractionDigits: 4 })} kg / ${zeile.unitCode || 'Einheit'}` : ''
  const nummern = [zeile.articleNumber, zeile.supplierArticleNumber ? `Lief.-ArtNr ${zeile.supplierArticleNumber}` : null].filter(Boolean).join(' · ')
  const zuInfo = !text && (zeile.altQuantity !== null || zeile.altUnitCode || nummern || positionText || gewichtText) && (
    <>
      <RasterInfo von="quantity" rechts>
        <span className="pr-px tabular-nums">{zeile.altQuantity !== null ? mengeText(zeile.altQuantity) : ''}</span>
      </RasterInfo>
      <RasterInfo von="unitCode">{zeile.altUnitCode ?? ''}</RasterInfo>
      <RasterInfo von="name">{nummern}</RasterInfo>
      <RasterInfo von="unitPrice" bis="lineNet">
        {[positionText, gewichtText].filter(Boolean).join(' · ')}
      </RasterInfo>
    </>
  )

  const hinweis = (zeile.sourceLineId || zeile.deviations.length > 0 || (zeile.articleId && !text) || kiVorschlag) && (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
      {kiVorschlag && (
        <KontoVorschlag
          vorschlag={kiVorschlag}
          konten={rahmen.ledgerAccounts}
          laeuft={kiKonto.laeuft}
          onUebernehmen={() => void kiKonto.uebernehmen({ lineIds: [zeile.id] })}
          onVerwerfen={() => void kiKonto.verwerfen({ lineIds: [zeile.id] })}
        />
      )}
      {zeile.articleId && !text && (
        <label className="flex items-center gap-1" title="Schreibt den Einzelpreis nach Rabatt bei der Freigabe als Einkaufspreis dieses Lieferanten in den Artikelstamm">
          <input type="checkbox" {...preisUebernehmen.schalter} /> Einkaufspreis übernehmen{zeile.articleNumber ? ` (${zeile.articleNumber})` : ''}
        </label>
      )}
      {zeile.sourceLineId && <span>↳ Bestellung {zeile.sourceDocumentNumber ?? ''}</span>}
      {zeile.sourceLineId && zeile.sourceUnitPrice !== null && zeile.deviations.some((d) => d.startsWith('PRICE')) && (
        <span>
          vereinbart {geldText(zeile.sourceUnitPrice)}
          {zeile.sourcePriceUnit && zeile.sourcePriceUnit !== 1 ? ` / ${zeile.sourcePriceUnit}` : ''}
          {zeile.sourceDiscountPercent ? ` −${mengeText(zeile.sourceDiscountPercent)} %` : ''}
        </span>
      )}
      {zeile.sourceLineId && !zeile.pairingConfirmed && <span className="rounded bg-sky-500/15 px-1 text-sky-700 dark:text-sky-300">vorgeschlagen</span>}
      {zeile.deviations.map((d) => (
        <span key={d} className={`rounded px-1 ${ABWEICHUNG_TEXT[d]?.klasse ?? 'bg-muted'}`}>
          {ABWEICHUNG_TEXT[d]?.label ?? d}
        </span>
      ))}
      {!gesperrt && zeile.sourceLineId && !zeile.pairingConfirmed && (
        <button type="button" className="underline hover:text-foreground" onClick={() => void bezug(() => confirmPairings(rechnung.id, [zeile.id]))}>
          Bestätigen
        </button>
      )}
      {!gesperrt && zeile.sourceLineId && (
        <button type="button" className="underline hover:text-foreground" onClick={() => void bezug(() => setLineSource(rechnung.id, zeile.id, null, zeile.updatedAt))} title="Die Position bleibt, nur der Bezug zur Bestellung wird gelöst">
          Bezug lösen
        </button>
      )}
    </span>
  )

  return (
    <ZeilenBlock
      gruppe={gruppe}
      schluessel={zeile.id}
      zeilen={zeile3 ? [zeile2, zeile3] : [zeile2]}
      unterzeile={unterzeile}
      zuInfo={zuInfo || undefined}
      hinweis={hinweis || undefined}
      aufklappbar={gesperrt}
      ziehbar={!gesperrt}
      markiert={markiert}
      gezogen={gezogen}
      ziellinie={ziellinie}
      onRand={onRand}
      randLabel={zeile.name || `Position ${zeile.positionNo}`}
    >
      <Spalte spalte="quantity" leer={text}>
        {!text && <input aria-label="Menge" data-start inputMode="decimal" className={`${zelle} text-right`} {...menge.props} />}
      </Spalte>
      <Spalte spalte="unitCode" leer={text}>
        {!text && <input aria-label="Einheit" list={EINHEITEN_LISTE} className={zelle} {...einheit.props} />}
      </Spalte>
      <Spalte spalte="name">
        <div className="relative">
          <input
            aria-label="Bezeichnung"
            className={`${zelle} font-medium`}
            title={String(name.props.value ?? '')}
            {...(text ? { 'data-start': true } : {})}
            {...name.props}
            onChange={(e) => {
              name.props.onChange(e)
              setSucheName(!text)
            }}
            onBlur={() => {
              name.props.onBlur()
              setSucheName(false)
              setArtikelTreffer([])
            }}
          />
          <Vorschlaege eintraege={artikelTreffer} schluessel={(a) => a.id} onWahl={uebernimm} darstellung={artikelZeile} />
        </div>
      </Spalte>
      <Spalte spalte="unitPrice" label="Einzelpreis" leer={text}>
        {!text && <GeldFeldFuer aria-label="Einzelpreis" className={zelle} stellen={4} feld={preis.props} />}
      </Spalte>
      <Spalte spalte="priceUnit" leer={text}>
        {!text && <input aria-label="Preiseinheit" title="Preiseinheit" inputMode="numeric" className={`${zelle} text-right`} {...pe.props} />}
      </Spalte>
      <Spalte spalte="discountPercent" label="Rabatt %" leer={text}>
        {!text && <input aria-label="Rabatt in Prozent" title="Rabatt in %" inputMode="decimal" className={`${zelle} px-1 text-right`} {...rabatt.props} />}
      </Spalte>
      <Spalte spalte="lineNet" leer={text}>
        {!text && <span className="block px-1.5 py-1 text-right text-sm tabular-nums">{geldText(netto)}</span>}
      </Spalte>
      <Spalte spalte="aktion">
        {!gesperrt && (
          <button type="button" className="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" onClick={() => void loeschen()} title="Position löschen" aria-label="Position löschen">
            <Trash2 size={14} />
          </button>
        )}
      </Spalte>
    </ZeilenBlock>
  )
}

const artikelZeile = (a: LookupArticle) => (
  <>
    <span className="font-medium">{a.name}</span>
    <span className="ml-2 text-xs text-muted-foreground">{a.articleNumber}</span>
  </>
)

/** Projekt suchen: Der Text ist nur die Suche — gespeichert wird die Wahl aus der Liste. */
function ProjektFeld({ anzeige, gewaehlt, gesperrt, onWahl, label, klasse = zelle }: { anzeige: string; gewaehlt: string | null; gesperrt: boolean; onWahl: (p: LookupProject) => void; label: string; klasse?: string }) {
  const [text, setText] = useState<string | null>(null)
  const [treffer, setTreffer] = useSuche(text ?? '', text !== null, lookupProjects)
  useEffect(() => setText(null), [gewaehlt])
  return (
    <span className="relative block min-w-0">
      <input
        aria-label={label}
        className={klasse}
        disabled={gesperrt}
        placeholder={`${label} suchen …`}
        title={anzeige}
        value={text ?? anzeige}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setText(null)
          setTreffer([])
        }}
      />
      <Vorschlaege
        eintraege={treffer}
        schluessel={(p) => p.id}
        onWahl={(p) => {
          setTreffer([])
          setText(null)
          onWahl(p)
        }}
        darstellung={(p) => (
          <span>
            <span className="tabular-nums">{p.projectNumber}</span> {p.name}
          </span>
        )}
      />
    </span>
  )
}

/** Die Positionen des gewählten Projekts — leer, solange keines gewählt ist oder es keine hat. */
function usePositionen(projectId: string | null): LookupPosition[] {
  const [positionen, setPositionen] = useState<LookupPosition[]>([])
  useEffect(() => {
    if (!projectId) return setPositionen([])
    let aus = false
    lookupPositions(projectId)
      .then((p) => !aus && setPositionen(p))
      .catch(() => !aus && setPositionen([]))
    return () => {
      aus = true
    }
  }, [projectId])
  return positionen
}

const LEER = {
  name: '',
  description: '',
  quantity: '',
  unitCode: '',
  altQuantity: '',
  altUnitCode: '',
  weightPerUnit: '',
  supplierArticleNumber: '',
  unitPrice: '',
  priceUnit: '',
  discountPercent: '',
  taxCodeKey: '',
  costTarget: '' as IncomingCostTarget | '',
  projectId: '',
  projectAnzeige: '',
  projectPositionId: '',
  overheadCategoryId: '',
  expenseAccount: '',
}

/**
 * Die leere letzte Zeile: legt beim Verlassen an, sobald eine Bezeichnung oder ein Preis dasteht. Sie zeigt
 * beim Bearbeiten **dieselben** Felder wie eine angelegte Zeile (Auftraggeber 5.10.2026) — vorher waren
 * Preiseinheit, Rabatt, Steuer, Kostenziel und die Zeilen 2 und 3 erst nach dem Anlegen erreichbar.
 * Die Menge ist nicht vorbelegt; bleibt sie leer, wird 1 angelegt. Steuer, Kostenziel und Sachkonto ohne Wahl
 * nehmen die Vorgabe (Steuercode des Betriebs, Kostenziel und Sachkonto des Kopfs).
 */
function NeueZeile({
  rechnung,
  rahmen,
  mitKonto,
  onRechnung,
  onFehler,
}: {
  rechnung: IncomingInvoiceDetail
  rahmen: IncomingFrame
  mitKonto: boolean
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
}) {
  const terms = useTerminology()
  const [neu, setNeu] = useState(LEER)
  // Ein gewählter Artikel gilt, bis die Bezeichnung wieder getippt wird.
  const [artikel, setArtikel] = useState<LookupArticle | null>(null)
  const [sucheName, setSucheName] = useState(false)
  const [artikelTreffer, setArtikelTreffer] = useSuche(neu.name, sucheName, lookupArticles)
  const mitProjekt = neu.costTarget === 'PROJECT'
  const positionen = usePositionen(mitProjekt && rahmen.projectPositions ? neu.projectId || null : null)
  const verwerfen = () => {
    setNeu(LEER)
    setArtikel(null)
    setSucheName(false)
    setArtikelTreffer([])
  }
  // Gelesen wie in jeder angelegten Zeile (`leseZahl`): „1.234,50" und „12.5" sind beide Zahlen
  const zahl = (t: string) => leseZahl(t) ?? Number.NaN
  const optional = (t: string) => (t.trim() ? zahl(t) : undefined)
  // Auch die neue Zeile zeigt ihren Betrag, sobald ein Preis dasteht — eine leere Menge zählt 1, wie beim Anlegen.
  const netto = neu.unitPrice.trim() ? zeilenNetto(leseZahl(neu.quantity) ?? 1, leseZahl(neu.unitPrice), leseZahl(neu.priceUnit), leseZahl(neu.discountPercent)) : null

  const anlegen = async () => {
    const preis = optional(neu.unitPrice) ?? 0
    if (!neu.name.trim() && !preis) return
    const zahlen = { quantity: optional(neu.quantity) ?? 1, unitPrice: preis, altQuantity: optional(neu.altQuantity), weightPerUnit: optional(neu.weightPerUnit), priceUnit: optional(neu.priceUnit), discountPercent: optional(neu.discountPercent) }
    if (Object.values(zahlen).some((n) => n !== undefined && Number.isNaN(n))) return onFehler('Menge, Preis, Preiseinheit, Rabatt und Gewicht müssen Zahlen sein.')
    if (zahlen.priceUnit !== undefined && (!Number.isInteger(zahlen.priceUnit) || zahlen.priceUnit < 1)) return onFehler('Die Preiseinheit ist eine ganze Zahl ab 1.')
    const text = (t: string) => (t.trim() ? t.trim() : undefined)
    const felder = {
      name: neu.name.trim(),
      ...zahlen,
      description: text(neu.description),
      unitCode: text(neu.unitCode),
      altUnitCode: text(neu.altUnitCode),
      supplierArticleNumber: text(neu.supplierArticleNumber),
      taxCodeKey: text(neu.taxCodeKey),
      // Ohne Wahl gilt das Sachkonto der Rechnung, sonst der Vorschlag
      expenseAccount: text(neu.expenseAccount),
      articleId: artikel?.id,
      // Ohne Wahl gilt die Vorgabe des Kopfs — dann geht kein Kostenziel mit
      ...(neu.costTarget && {
        costTarget: neu.costTarget,
        ...(neu.costTarget === 'PROJECT' && { projectId: text(neu.projectId), projectPositionId: text(neu.projectPositionId) }),
        ...(neu.costTarget === 'OVERHEAD' && { overheadCategoryId: text(neu.overheadCategoryId) }),
      }),
    }
    try {
      onFehler(null)
      onRechnung(await createIncomingLine(rechnung.id, Object.fromEntries(Object.entries(felder).filter(([, w]) => w !== undefined))))
      verwerfen()
    } catch (e) {
      onFehler(apiErrorText(e, 'Die Position ließ sich nicht anlegen.'))
    }
  }

  type Textfeld = Exclude<keyof typeof LEER, 'costTarget'>
  const feld = (key: Textfeld) => ({ value: neu[key], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setNeu((alt) => ({ ...alt, [key]: e.target.value })) })

  // Die Unterzeile wie an einer angelegten Position — an der leeren Zeile erst beim Bearbeiten zu sehen.
  const unterzeile = (
    <Unterzeile
      steuer={
        <select aria-label="Steuercode" className={klein} title="Ohne Wahl gilt der Steuercode des Betriebs" {...feld('taxCodeKey')}>
          <option value="">Steuer</option>
          {rahmen.taxCodes.map((c) => (
            <option key={c.key} value={c.key} title={c.label}>
              {c.key}
            </option>
          ))}
        </select>
      }
      ziel={
        <select
          aria-label="Kostenziel"
          className={klein}
          title="Ohne Wahl gilt das Standard-Kostenziel aus dem Kopf"
          value={neu.costTarget}
          onChange={(e) => setNeu((alt) => ({ ...alt, costTarget: e.target.value as IncomingCostTarget | '' }))}
        >
          <option value="">Kostenziel</option>
          {ZIELE.map((z) => (
            <option key={z} value={z}>
              {kostenzielText(z, terms.project)}
            </option>
          ))}
        </select>
      }
      konto={
        !mitKonto ? null : rahmen.ledgerAccounts.length > 0 ? (
          <select aria-label="Sachkonto" className={`${klein} tabular-nums`} title="Ohne Wahl gilt das Sachkonto der Rechnung, sonst der Vorschlag" {...feld('expenseAccount')}>
            <option value="">{rechnung.defaultExpenseAccount ? `wie Rechnung (${rechnung.defaultExpenseAccount})` : 'Sachkonto'}</option>
            <KontoOptionen konten={rahmen.ledgerAccounts} aktuell={null} />
          </select>
        ) : (
          <input aria-label="Sachkonto" placeholder="Sachkonto" className={`${klein} tabular-nums`} {...feld('expenseAccount')} />
        )
      }
      zuordnung={
        mitProjekt ? (
          <ProjektFeld klasse={klein} gesperrt={false} anzeige={neu.projectAnzeige} gewaehlt={neu.projectId || null} onWahl={(p) => setNeu((alt) => ({ ...alt, projectId: p.id, projectAnzeige: `${p.projectNumber} ${p.name}`, projectPositionId: '' }))} label={terms.project} />
        ) : neu.costTarget === 'OVERHEAD' ? (
          <select aria-label="Gemeinkosten-Kategorie" className={klein} {...feld('overheadCategoryId')}>
            <option value="">Kategorie wählen …</option>
            {rahmen.overheadCategories.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
        ) : null
      }
      zuordnungLabel={mitProjekt ? terms.project : 'Kategorie'}
    />
  )
  const zeile2 = (
    <>
      <RasterFeld von="quantity" label="Alternativmenge" schmal="w-24">
        <input className={`${zelle} text-right`} inputMode="decimal" {...feld('altQuantity')} />
      </RasterFeld>
      <RasterFeld von="unitCode" label={null} schmal="w-20">
        <input aria-label="Alternativeinheit" list={EINHEITEN_LISTE} className={zelle} {...feld('altUnitCode')} />
      </RasterFeld>
      <RasterFeld von="name" label="Langtext" schmal="w-full max-w-xl">
        <textarea rows={2} className={zelle} {...feld('description')} />
      </RasterFeld>
      {mitProjekt && rahmen.projectPositions && positionen.length > 0 && (
        <RasterFeld von="unitPrice" bis="lineNet" label={terms.position} schmal="w-56">
          <select className={zelle} {...feld('projectPositionId')}>
            <option value="">ohne {terms.position}</option>
            {positionen.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number} {p.name}
              </option>
            ))}
          </select>
        </RasterFeld>
      )}
    </>
  )
  const zeile3 = (
    <>
      <RasterFeld von="quantity" bis="unitCode" label="kg je Einheit" schmal="w-40">
        <span className="flex items-center gap-1.5">
          <input className={`${zelle} text-right`} inputMode="decimal" {...feld('weightPerUnit')} />
          <span className="whitespace-nowrap text-xs text-muted-foreground">kg / {neu.unitCode || 'Einheit'}</span>
        </span>
      </RasterFeld>
      <RasterFeld von="name" label="ArtNr · Lief.-ArtNr" schmal="w-full max-w-xl">
        <span className="flex items-center gap-2">
          <span className="w-36 shrink-0 truncate px-1.5 py-1 font-mono text-sm" title="Kommt aus dem Artikelstamm — in der Bezeichnung einen Artikel suchen und wählen">
            {artikel?.articleNumber ?? <span className="text-muted-foreground">—</span>}
          </span>
          <input aria-label="Lieferanten-Artikelnummer" placeholder="Artikelnummer des Lieferanten" className={`${zelle} min-w-0 flex-1`} title="Artikelnummer des Lieferanten" {...feld('supplierArticleNumber')} />
        </span>
      </RasterFeld>
    </>
  )

  return (
    <ZeilenBlock neu zeilen={[unterzeile, zeile2, zeile3]} onVerlassen={() => void anlegen()} onVerwerfen={verwerfen}>
      <Spalte spalte="quantity">
        <input aria-label="Menge" data-start inputMode="decimal" className={`${zelle} text-right`} {...feld('quantity')} />
      </Spalte>
      <Spalte spalte="unitCode">
        <input aria-label="Einheit" list={EINHEITEN_LISTE} className={zelle} {...feld('unitCode')} />
      </Spalte>
      <Spalte spalte="name">
        <div className="relative">
          <input
            aria-label="Neue Position"
            className={zelle}
            placeholder="Neue Position — Artikel suchen oder Bezeichnung eingeben"
            value={neu.name}
            onChange={(e) => {
              setNeu((alt) => ({ ...alt, name: e.target.value }))
              setArtikel(null)
              setSucheName(true)
            }}
            onBlur={() => {
              setSucheName(false)
              setArtikelTreffer([])
            }}
          />
          <Vorschlaege
            eintraege={artikelTreffer}
            schluessel={(a) => a.id}
            onWahl={(a) => {
              setSucheName(false)
              setArtikelTreffer([])
              setArtikel(a)
              setNeu((alt) => ({ ...alt, name: a.name, unitCode: alt.unitCode || a.unitCode, description: alt.description || (a.documentText ?? '') }))
            }}
            darstellung={artikelZeile}
          />
        </div>
      </Spalte>
      <Spalte spalte="unitPrice" label="Einzelpreis">
        <GeldFeld aria-label="Einzelpreis" className={zelle} stellen={4} wert={neu.unitPrice} onWert={(t) => setNeu((alt) => ({ ...alt, unitPrice: t }))} />
      </Spalte>
      <Spalte spalte="priceUnit">
        <input aria-label="Preiseinheit" title="Preiseinheit" inputMode="numeric" placeholder="1" className={`${zelle} text-right`} {...feld('priceUnit')} />
      </Spalte>
      <Spalte spalte="discountPercent" label="Rabatt %">
        <input aria-label="Rabatt in Prozent" title="Rabatt in %" inputMode="decimal" className={`${zelle} px-1 text-right`} {...feld('discountPercent')} />
      </Spalte>
      <Spalte spalte="lineNet" leer={netto === null}>
        {netto !== null ? <span className="block px-1.5 py-1 text-right text-sm tabular-nums">{geldText(netto)}</span> : <span />}
      </Spalte>
      <Spalte spalte="aktion" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  )
}
